import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import {
  FormFieldList,
  SfFileUploadFormField,
  SfSelectFormField,
  SfSelectOption,
  SfUtils,
  SmartFormComponent,
} from 'ngx-smart-form';
import {
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Pratica, StatoPratica } from '../../interfaces/pratica';
import { DialogService } from '../_core/services/dialog.service';
import { HistoryService } from '../_core/services/history.service';
import { PraticaProgressService } from '../_core/services/pratica-progress.service';
import { HistoryTask, Task, TaskService } from '../_core/services/task.service';
import { ServerSideTableComponent } from '../_widgets/server-side-table/server-side-table.component';
import { CmsField, ICmsField } from '../_core/models/cms-field';
import { FormControl, FormGroup } from '@angular/forms';
import { DocumentsService } from '../_core/services/documents.service';
import { ICommand } from '../_shared/models/commands/i-command';
import { SeeDocumentCommand } from '../_shared/models/commands/see-document-command';
import { DownloadDocumentCommand } from '../_shared/models/commands/download-document-command';
import { UploadedDocument } from '../_core/models/uploaded-document';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TaskManagerService } from '../pratica/task-manager.service';
import { History } from '../history/history';
import { EventService } from '../_core/services/event.service';
import { IconService } from '../_core/services/icon.service';
import { isDocument } from './document-type-guard';
import { SseDocument } from './sse-document';
@Component({
  selector: 'app-task-manager',
  templateUrl: './task-manager.component.html',
  styleUrls: ['./task-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class TaskManagerComponent extends ServerSideTableComponent<History> {
  @ViewChild('test') test?: TemplateRef<any>;
  @ViewChild('dependencyFormComponent')
  dependencyFormComponent?: SmartFormComponent;
  isLoading$ = new Subject<boolean>();
  name: string = '';
  type: string = '';

  expandedElement: any | undefined;

  hasExecutedTask = false;

  innerDisplayedColumns: (keyof UploadedDocument | 'actions')[] = [
    'nome',
    'tipo',
    'actions',
  ];

  taskFormControl = new FormControl<string>('');

  private _actionTypeFields: FormFieldList = [];

  set actionTypeFields(f: FormFieldList) {
    this._actionTypeFields = f;
    this.taskManager.actionTypeFields = f;
  }

  get actionTypeFields() {
    return this._actionTypeFields;
  }

  private _dependencyFields: FormFieldList = [];

  set dependencyFields(f: FormFieldList) {
    this._dependencyFields = f;
    this.taskManager.dependencyFields = f;
  }

  get dependencyFields() {
    return this._dependencyFields;
  }

  actions: Task[] = [];

  taskFormTitle = '';

  selectedTabIndex = 0;

  commands: ICommand[];
  @Input() data: Pratica | undefined;
  @Output() close = new EventEmitter<boolean>();
  constructor(
    inj: Injector,
    private taskService: TaskService,
    private dialogService: DialogService,
    private praticaProgressService: PraticaProgressService,
    private documentsService: DocumentsService,
    private taskManager: TaskManagerService,
    private eventService: EventService,
    private iconService: IconService
  ) {
    super(inj, HistoryService);
    this.name = this.data?.piva || '';
    this.type = this.data?.target?.toUpperCase() || '';
    this.commands = [
      new SeeDocumentCommand(inj, 'pdf-viewer'),
      new DownloadDocumentCommand(inj),
    ];
  }

  override ngOnInit(): void {
    if (this.taskManager.tasks) {
      this.manageTasks(this.taskManager.tasks);
      if (this.taskManager.taskCtrlValue) {
        this.taskFormControl.patchValue(this.taskManager.taskCtrlValue);
      }
      if (this.taskManager.dependencyFormValue) {
        this.dependencyFormComponent?.form.patchValue(
          this.taskManager.dependencyFormValue
        );
      }
      this.taskFormTitle = this.taskManager.taskFormTitle;
      this.selectedTabIndex = this.taskManager.selectedTabIndex;
    } else {
      this.updatePratica().subscribe();
    }

    if (
      this.praticaProgressService.selectedPratica &&
      !this.taskManager.praticaCompletionSubscription
    )
      this.taskManager.praticaCompletionSubscription =
        this.praticaProgressService.isReadyForSignature$
          .pipe(
            skip(1),
            distinctUntilChanged(),
            switchMap((isReady) => {
              return this.updatePratica();
            })
          )
          .subscribe();
  }

  override searchOnPaginationEvent() {
    if (this.taskManager.taskCtrlValue && this.taskManager.tableData) {
      this.dataSource.data = this.taskManager.tableData!.data;
      this.totalData = this.taskManager.totalData;
      this.paginator!.pageIndex = this.taskManager.tablePage!;

      return this.paginator!.page.pipe(
        switchMap(() => {
          return this.getFilterData$(
            this.paginator?.pageIndex,
            this.paginator?.pageSize,
            this.searchFormComponent?.formRawValue
          );
        })
      );
    } else {
      return super.searchOnPaginationEvent();
    }
  }

  override ngOnDestroy(): void {
    this.taskManager.storeFormValue(
      this.taskFormControl.value || undefined,
      this.taskFormTitle,
      this.dependencyFormComponent?.formRawValue,
      this.paginator?.pageIndex,
      this.dataSource,
      this.totalData,
      this.selectedTabIndex
    );
    super.ngOnDestroy();
  }

  private updatePratica() {
    if (this.data) {
      const id = this.data.id;
      const req$ = this.praticaProgressService.selectedPratica
        ? this.praticaProgressService
            .updatePraticaProgress()
            .pipe(switchMap(() => this.praticaProgressService.getPratica(id)))
        : this.praticaProgressService
            .getPraticaById(id)
            .pipe(map((pratica) => new Pratica(pratica)));

      return req$.pipe(
        switchMap((pratica) => {
          let richiestaStates = [
            StatoPratica.APERTA,
            StatoPratica.COMPLETATA,
            StatoPratica.IN_DELIBERA_CDA,
          ];

          let processId =
            richiestaStates.includes(pratica.state) || pratica.type === 'AML'
              ? pratica.process?.richiesta
              : pratica.process?.istruttoria!;
          return processId
            ? this.getTasks(processId, pratica.id)
            : throwError(() => 'Processo non trovato');
        })
      );
    } else {
      return of([]);
    }
  }

  private getTasks(processId: string, praticaId: string) {
    return this.taskService.tasks(processId, praticaId).pipe(
      tap((res) => (this.taskManager.tasks = res as HistoryTask[])),
      tap((res) => this.manageTasks(res as HistoryTask[]))
    );
  }

  private manageTasks(tasks: HistoryTask[]) {
    this.actions = tasks;
    this.taskFormControl.reset();
    this.taskFormTitle = '';
    if (this.dependencyFormComponent) {
      this.dependencyFormComponent.form = new FormGroup({});
    }
    this.cdr.detectChanges();
    this.generateForm(tasks);
  }

  protected override search(
    pageNumber: number,
    pageSize: number,
    filter?: Partial<History> | undefined
  ): Observable<{ total: number; items: any }> {
    return this.dataService.search(
      pageNumber + 1,
      pageSize,
      filter,
      this.data?.id
    );
  }

  executeTask(task: Task) {
    let subscriberid = uuidv4();
    let userInputData: any = {
      action: this.taskFormControl.value,
      subscriber: subscriberid,
      ...this.dependencyFormComponent?.formRawValue,
    };

    let documentData = { ...userInputData };

    let uploadFields = [
      ...this.actionTypeFields.flat(),
      ...this.dependencyFields.flat(),
    ]
      .filter((field) => field instanceof SfFileUploadFormField)
      .map((f) => f.formControlName);

    for (let key in documentData) {
      if (!uploadFields.includes(key)) {
        delete documentData[key];
      }
    }
    this.documentsService
      .multiUpload(documentData, uuidv4(), undefined, this.data?.id)
      .pipe(
        tap((idDocs) => {
          idDocs.forEach((doc) => {
            userInputData[doc.name] = doc.id;
          });
        }),
        switchMap((_) => {
          this.isLoading$.next(true);
          let taskBody: Task = { ...task, variables: userInputData };

          return forkJoin([
            this.subscribeSse<
              { message: string; documents?: SseDocument[] },
              { message: string }
            >(subscriberid),
            this.taskService.execute(taskBody),
          ]);
        }),

        tap(([sseResp]) => {
          this.dialogService.onSuccess();
          this.handleSseSuccess(sseResp);
        }),
        catchError((error) => {
          return this.dialogService.onError(error, 'Riprova').afterDismissed();
        }),
        tap((_) => this.isLoading$.next(false)),
        switchMap((_) => this.updatePratica()),
        switchMap((_) => this.getFilterData$()),
        tap((_) => (this.hasExecutedTask = true))
      )
      .subscribe();
  }

  private subscribeSse<R = object, E = any>(
    subscriberId: string
  ): Observable<R | undefined> {
    this.isLoading$.next(true);
    return this.eventService
      .getServerEvent<R, E>(
        subscriberId,
        'TASK_EVENT_TYPE_OK',
        'TASK_EVENT_TYPE_KO'
      )
      .pipe(
        tap((res) => {
          console.log(res);
          //this.isLoading = false;
        })
      );
  }

  private generateForm(tasks: HistoryTask[]) {
    let fields: FormFieldList = [];
    let actionFields =
      tasks[0]?.variables.filter((f) => f.name === 'action') || [];
    let dependencyFields =
      tasks[0]?.variables.filter(
        (f) => (!f.parent && f.name !== 'action') || f.parent === 'action'
      ) || [];
    fields = CmsField.genFieldList(actionFields);
    if (tasks.length === 1 && fields.length > 0) {
      this.actionTypeFields = fields;
      let iconOption$ = this.taskField?.options?.map((el) =>
        this.iconService.addIcon(el.value, 'DELIBERA', 'pratica-states')
      );
      let ob$ = iconOption$ || [of()];
      forkJoin(ob$).subscribe((iconOptions) => {
        if (this.taskField) {
          this.taskField.options = iconOptions.map((el, i) => ({
            value: this.taskField?.options?.[i].value || '',
            view: this.taskField?.options?.[i].view || '',
            icon: el,
          }));
        }
      });
      this.cdr.detectChanges();
      this.taskFormControl.setValue(this.taskField?.value);
      this.taskFormTitle =
        this.taskField?.options?.find(
          (el) => el.value === this.taskField?.value
        )?.view || '';
      this.taskFormControl.valueChanges
        .pipe(
          startWith(this.taskField?.value),
          filter((val) => !!val)
        )
        .subscribe((val) => {
          this.genDependencyForm(dependencyFields, val);
          this.cdr.detectChanges();
        });
    } else if (tasks[0]?.variables) {
      fields = CmsField.genFieldList(tasks[0].variables);
      this.dependencyFields = fields;
      this.cdr.detectChanges();
    } else {
      this.actionTypeFields = [];
      this.dependencyFields = [];
      this.cdr.detectChanges();
    }
  }

  private genDependencyForm(dependencyFields: ICmsField[], val: any) {
    if (this.dependencyFormComponent) {
      this.dependencyFormComponent.form = new FormGroup({});
    }
    let filteredDependencies = dependencyFields.filter(
      (f) => !f.parent || f.parentMatchValue === val
    );
    this.dependencyFields = CmsField.genFieldList(filteredDependencies);
  }

  get formIsValid() {
    return (
      (this.dependencyFormComponent?.form?.valid &&
        this.taskFormControl.valid) ||
      false
    );
  }

  closeDialog() {
    this.close.emit(this.hasExecutedTask);
  }

  get taskField() {
    return SfUtils.getField('action', this.actionTypeFields) as
      | SfSelectFormField
      | undefined;
  }

  get taskFieldOptions() {
    return this.taskField?.options as (SfSelectOption & { icon: string })[];
  }

  setAction(option: SfSelectOption) {
    this.taskFormControl.setValue(option.value);
    this.taskFormTitle = option.view;
  }

  private handleSseSuccess(
    sseResp:
      | {
          message: string;
          documents?: SseDocument[];
        }
      | undefined
  ) {
    if (sseResp && sseResp.documents && Array.isArray(sseResp.documents)) {
      sseResp.documents.forEach((doc) => {
        if (isDocument(doc)) {
          new DownloadDocumentCommand(this.inj)
            .execute({ ...doc, nome: doc.name })
            .subscribe();
        }
      });
    }
  }
}
